import { type ImageLayout, type ImageProps } from './types.js';
import { css, styled } from 'styled-components';

type ContainerProps = {
  $height?: number;
  $width?: number;
  layout: ImageLayout;
};

const getContainerStyles = ({ $height, $width, layout }: ContainerProps) => {
  switch (layout) {
    case 'fill': {
      return css`
        display: block;

        position: absolute;
        top: 0;
        left: 0;
        bottom: 0;
        right: 0;
      `;
    }

    case 'fixed': {
      if (typeof $height === 'number' && typeof $width === 'number') {
        return css`
          display: inline-block;
          position: relative;
          width: ${$width}px;
          height: ${$height}px;
        `;
      }

      throw new Error(
        '`height` and `width` are required on `fixed` image layout',
      );
    }

    case 'intrinsic': {
      return css`
        display: inline-block;
        position: relative;
        max-width: 100%;
      `;
    }

    case 'responsive': {
      return css`
        display: block;
        position: relative;
      `;
    }

    default: {
      throw new Error(`Unsupported image layout type '${layout}'`);
    }
  }
};

export const Container = styled.div<ContainerProps>`
  box-sizing: border-box;
  margin: 0;
  overflow: hidden;

  ${({ $height, $width, layout }) =>
    getContainerStyles({ $height, $width, layout })}
`;

/**
 * Sizer should only be rendered for `intrinsic` and `responsive` layout types.
 */
export const Sizer = styled.div<{
  $height: number;
  $width: number;
  layout: 'intrinsic' | 'responsive';
}>`
  display: block;
  box-sizing: border-box;
  max-width: 100%;
  padding-top: ${({ $height, $width, layout }) =>
    layout === 'responsive'
      ? `${(($height / $width) * 100).toFixed(3)}%`
      : '0'};
`;

export const StyledImage = styled.img<
  Pick<ImageProps, 'objectFit' | 'objectPosition'>
>`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;

  box-sizing: border-box;
  padding: 0;
  border: none;
  margin: auto;

  display: block;
  width: 0;
  height: 0;
  min-width: 100%;
  max-width: 100%;
  min-height: 100%;
  max-height: 100%;

  object-fit: ${({ objectFit }) => (objectFit ? objectFit : 'initial')};
  object-position: ${({ objectPosition }) =>
    objectPosition ? objectPosition : 'initial'};
`;

export const SizerImage = styled.img`
  display: block;
  max-width: 100%;
  border: 0;
  margin: 0;
  padding: 0;
`;
